import { useCallback } from 'react';
import { usePostApiDeliverablesByIdCancelMutation } from '../../app/athenaApiGen';

export type CancelNotifySeverity = 'success' | 'error' | 'warning' | 'info';

export interface CancelableFormRow {
  id?: string;
  workflowState?: string | null;
}

interface UseCancelFormDeliverableOptions {
  entityLabel: string;
  notify: (message: string, severity: CancelNotifySeverity) => void;
  refetch: () => unknown;
}

// A form's id is also its deliverable id, so form list pages cancel through the
// deliverable cancel endpoint (resets tasks, clears DateSubmitted, writes the
// "Returned" audit). The workflow PATCH route rejects a reset to Pending.
export function useCancelFormDeliverable({
  entityLabel,
  notify,
  refetch,
}: UseCancelFormDeliverableOptions) {
  const [cancelDeliverable] = usePostApiDeliverablesByIdCancelMutation();

  const handleCancelClick = useCallback(
    async (row: CancelableFormRow) => {
      if (!row.id) return;
      if (!row.workflowState || row.workflowState === 'Pending') {
        notify(`Cannot cancel: ${entityLabel} has not been submitted yet.`, 'warning');
        return;
      }
      try {
        await cancelDeliverable({ id: row.id }).unwrap();
        notify(`${entityLabel} cancelled successfully`, 'success');
        refetch();
      } catch (error) {
        console.error(`Failed to cancel ${entityLabel}`, error);
        const data = (error as { data?: { details?: string; error?: string } } | undefined)
          ?.data;
        const errorMsg =
          data?.details || data?.error || `Failed to cancel ${entityLabel}. Please try again.`;
        notify(errorMsg, 'error');
      }
    },
    [cancelDeliverable, entityLabel, notify, refetch],
  );

  return { handleCancelClick };
}
